// src/screens/TrainerEditAvailability.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Title from "../components/Title";
import TabBar from "../components/TabBar";
import { PrimaryButton } from "../components/Button";
import { Plus, X } from "lucide-react";
import TimePicker from "./TimePicker";
import DateSpecificHoursModal, { TimeRange as DSRange } from "../components/DateSpecificHoursModal";
import UnavailableDatesModal from "../components/UnavailableDatesModal";

import CalendarMonth from "../components/CalendarMonth";
import DaySlotsPreview from "../components/DaySlotsPreview";
import PreviewMonth from "../components/PreviewMonth";
import PreviewDaySlots from "../components/PreviewDaySlots";
import { updateTrainerAvailability, getTrainerProfile } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { utcTimeOfDayToLocal, localTimeOfDayToUTC, formatTimeSlot } from "../utils/timeUtils";

/* --------------------------------- types ---------------------------------- */
type DayKey =
  | "Sunday" | "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday";

type TimeRange = { id: string; startTime: string; endTime: string; };
type DayAvailability = { enabled: boolean; timeRanges: TimeRange[]; };
type AvailabilityState = Record<DayKey, DayAvailability>;

const DAY_KEYS: DayKey[] = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/* ------------------------------ helpers ----------------------------------- */
const fmtMonthDayYear = (iso: string) => {
  const [y, m, d] = iso.split("-").map((n) => +n);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const sortISO = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/* ------------------------------ UI atoms ---------------------------------- */
function SquareCheckbox({ checked, onChange, ariaLabel }: {
  checked: boolean; onChange: (v: boolean) => void; ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      aria-label={ariaLabel}
      className={[
        "grid place-items-center border border-solid box-border flex-shrink-0",
        checked ? "bg-[#EB2726] border-[#EB2726]" : "bg-white border-[#CFCFCF]",
      ].join(" ")}
      style={{ width: '20px', height: '20px' }}
    >
      {checked ? (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 6.5 5 8.5 9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ) : null}
    </button>
  );
}

function TimeRangeChip({
  timeRange, onStartTimeClick, onEndTimeClick, onRemove,
}: {
  timeRange: TimeRange; onStartTimeClick: () => void; onEndTimeClick: () => void; onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onStartTimeClick}
          className="h-[40px] px-3 border border-[#DEDEDE] bg-white text-[14px] text-[#2D2D2D]">
          {timeRange.startTime}
        </button>
        <button type="button" onClick={onEndTimeClick}
          className="h-[40px] px-3 border border-[#DEDEDE] bg-white text-[14px] text-[#2D2D2D]">
          {timeRange.endTime}
        </button>
      </div>
      <button type="button" className="w-6 h-6 bg-white border border-[#DEDEDE] grid place-items-center"
        onClick={onRemove} aria-label="Remove time range">
        <X className="w-3.5 h-3.5 text-[#8A8A8A]" />
      </button>
    </div>
  );
}

function DayCard({
  day, state, onToggle, onAddTimeRange, onRemoveTimeRange, onStartTimeClick, onEndTimeClick,
}: {
  day: DayKey; state: DayAvailability; onToggle: (enabled: boolean) => void;
  onAddTimeRange: () => void; onRemoveTimeRange: (rangeId: string) => void;
  onStartTimeClick: (rangeId: string) => void; onEndTimeClick: (rangeId: string) => void;
}) {
  return (
    <div className="border border-[#DEDEDE] bg-white px-4 py-3">
      <div className="flex items-center gap-2">
        <SquareCheckbox checked={state.enabled} onChange={onToggle} ariaLabel={`${day} availability`} />
        <div className="font-extrabold text-[14px] text-[#2D2D2D]">{day}</div>
      </div>

      {state.enabled ? (
        <>
          <div className="mt-3 flex flex-wrap gap-3">
            {state.timeRanges.map((timeRange) => (
              <TimeRangeChip
                key={timeRange.id}
                timeRange={timeRange}
                onStartTimeClick={() => onStartTimeClick(timeRange.id)}
                onEndTimeClick={() => onEndTimeClick(timeRange.id)}
                onRemove={() => onRemoveTimeRange(timeRange.id)}
              />
            ))}
          </div>
          <button type="button"
            className="mt-3 w-6 h-6 bg-white border border-[#DEDEDE] grid place-items-center"
            onClick={onAddTimeRange} aria-label="Add another time range">
            <Plus className="w-3.5 h-3.5 text-[#8A8A8A]" />
          </button>
        </>
      ) : (
        <div className="mt-2 text-[12px] text-[#8A8A8A]">Unavailable</div>
      )}
    </div>
  );
}

/* ----------------------- Session setting composite ------------------------ */
type SessionSettingProps = {
  title: string; valueText: string; onValueClick: () => void; children: React.ReactNode; helper: string;
};

function SessionSettingCard({
  title, valueText, onValueClick, children, helper, isEditing = false,
}: SessionSettingProps & { isEditing?: boolean }) {
  return (
    <div className={`flex flex-col gap-2 w-full ${isEditing ? "h-[156px]" : "h-[80px] pb-4"}`}>
      <div className={`flex flex-col w-full border border-[#B1B1B1] ${isEditing ? "p-4 gap-5 h-[116px]" : "px-4 pt-4 pb-8 h-[40px]"}`}>
        <div className="flex justify-between items-center w-full h-4">
          <div className="font-extrabold text-[14px]">{title}</div>
          <button type="button" onClick={onValueClick}
            className="text-[#D62422] text-[12px] font-extrabold underline">{valueText}</button>
        </div>
        {isEditing && <div className="flex items-start gap-5 w-full h-12">{children}</div>}
      </div>
      <div className="px-4 h-8 text-[12px] text-[#717171]">{helper}</div>
    </div>
  );
}

/* ---------------------------------- screen --------------------------------- */
export default function TrainerEditAvailability() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  // Consent
  const [autoAccept, setAutoAccept] = useState(true);

  // Values + edit modes
  const [advanceHours, setAdvanceHours] = useState<number>(3);
  const [coolOffHours, setCoolOffHours] = useState<number>(3);
  const [dailyLimit, setDailyLimit] = useState<number>(6);
  const [editingAdvance, setEditingAdvance] = useState(false);
  const [editingCoolOff, setEditingCoolOff] = useState(false);
  const [editingDaily, setEditingDaily] = useState(false);

  // Inputs refs
  const advanceRef = useRef<HTMLInputElement | null>(null);
  const coolOffRef = useRef<HTMLInputElement | null>(null);
  const dailyRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { if (editingAdvance) advanceRef.current?.focus(); }, [editingAdvance]);
  useEffect(() => { if (editingCoolOff) coolOffRef.current?.focus(); }, [editingCoolOff]);
  useEffect(() => { if (editingDaily) dailyRef.current?.focus(); }, [editingDaily]);

  // Load trainer's actual availability data
  useEffect(() => {
    const loadTrainerData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const trainerData = await getTrainerProfile(user.id);
        console.log('Loaded trainer data:', trainerData);
        console.log('Availability data:', trainerData.availability);
        console.log('Exceptions data:', trainerData.exceptions);

        // Load session settings
        if (trainerData.thresholdPeriod !== undefined) {
          setAdvanceHours(trainerData.thresholdPeriod);
        }
        if (trainerData.cooldownPeriod !== undefined) {
          setCoolOffHours(trainerData.cooldownPeriod);
        }
        if (trainerData.maxSessionsPerDay !== undefined) {
          setDailyLimit(trainerData.maxSessionsPerDay);
        }

        // Load availability data
        if (trainerData.availability && Array.isArray(trainerData.availability)) {
          console.log('Found availability array with', trainerData.availability.length, 'items');
          const newAvailability: AvailabilityState = {
            Sunday: { enabled: false, timeRanges: [] },
            Monday: { enabled: false, timeRanges: [] },
            Tuesday: { enabled: false, timeRanges: [] },
            Wednesday: { enabled: false, timeRanges: [] },
            Thursday: { enabled: false, timeRanges: [] },
            Friday: { enabled: false, timeRanges: [] },
            Saturday: { enabled: false, timeRanges: [] },
          };

          trainerData.availability.forEach((dayData: any) => {
            console.log('Processing day data:', dayData);
            const dayName = dayData.day as DayKey;
            console.log('Day name:', dayName);
            if (dayName && newAvailability[dayName]) {
              const hasTimeSlots = dayData.timeSlots && dayData.timeSlots.length > 0;
              console.log('Setting availability for', dayName, ':', {
                enabled: hasTimeSlots,
                timeSlots: dayData.timeSlots
              });
              newAvailability[dayName] = {
                enabled: hasTimeSlots, // Enable if there are time slots
                timeRanges: (dayData.timeSlots || []).map((slot: any, index: number) => {
                  // Convert UTC times to local time for display in input fields
                  let startTimeLocal = slot.startTime || "9:00 AM";
                  let endTimeLocal = slot.endTime || "5:00 PM";

                  // If times are in UTC 24-hour format, convert to local 12-hour format
                  if (!startTimeLocal.includes('AM') && !startTimeLocal.includes('PM') && startTimeLocal.includes(':')) {
                    startTimeLocal = utcTimeOfDayToLocal(startTimeLocal, '12hour');
                  }
                  if (!endTimeLocal.includes('AM') && !endTimeLocal.includes('PM') && endTimeLocal.includes(':')) {
                    endTimeLocal = utcTimeOfDayToLocal(endTimeLocal, '12hour');
                  }

                  return {
                    id: `${dayName}_${index}`,
                    startTime: startTimeLocal,
                    endTime: endTimeLocal,
                  };
                }),
              };
            }
          });

          console.log('Final availability state:', newAvailability);
          setAvailability(newAvailability);
        } else {
          console.log('No availability data found in trainer profile');
          // Keep the default availability state (all days disabled)
        }

        // Load exceptions/date overrides and unavailable dates
        if (trainerData.exceptions && Array.isArray(trainerData.exceptions)) {
          const newDateOverrides: Record<string, DSRange[]> = {};
          const newUnavailableOverrides: Record<string, DSRange[]> = {};

          trainerData.exceptions.forEach((exception: any) => {
            if (!exception.isAvailable && exception.date) {
              // Mark as unavailable date with time ranges
              let dateISO: string;
              if (exception.date instanceof Date) {
                dateISO = exception.date.toISOString().split('T')[0];
              } else if (typeof exception.date === 'string') {
                dateISO = exception.date.split('T')[0];
              } else {
                console.warn('Invalid date format in exception:', exception.date);
                return;
              }

              if (!newUnavailableOverrides[dateISO]) {
                newUnavailableOverrides[dateISO] = [];
              }
              // Convert UTC times to local time for display
              let startTimeLocal = exception.startTime || "9:00 AM";
              let endTimeLocal = exception.endTime || "5:00 PM";

              // If times are in UTC 24-hour format, convert to local 12-hour format
              const exceptionDate = exception.date ? new Date(exception.date) : new Date();
              if (!startTimeLocal.includes('AM') && !startTimeLocal.includes('PM') && startTimeLocal.includes(':')) {
                startTimeLocal = utcTimeOfDayToLocal(startTimeLocal, '12hour', exceptionDate);
              }
              if (!endTimeLocal.includes('AM') && !endTimeLocal.includes('PM') && endTimeLocal.includes(':')) {
                endTimeLocal = utcTimeOfDayToLocal(endTimeLocal, '12hour', exceptionDate);
              }

              newUnavailableOverrides[dateISO].push({
                id: `${dateISO}_${newUnavailableOverrides[dateISO].length}`,
                startTime: startTimeLocal,
                endTime: endTimeLocal,
              });
            } else if (exception.isAvailable && exception.date) {
              // Handle both Date objects and ISO strings
              let dateISO: string;
              if (exception.date instanceof Date) {
                dateISO = exception.date.toISOString().split('T')[0];
              } else if (typeof exception.date === 'string') {
                dateISO = exception.date.split('T')[0];
              } else {
                console.warn('Invalid date format in exception:', exception.date);
                return;
              }

              if (!newDateOverrides[dateISO]) {
                newDateOverrides[dateISO] = [];
              }
              // Convert UTC times to local time for display
              let startTimeLocal = exception.startTime || "9:00 AM";
              let endTimeLocal = exception.endTime || "5:00 PM";

              // If times are in UTC 24-hour format, convert to local 12-hour format
              const exceptionDate = exception.date ? new Date(exception.date) : new Date();
              if (!startTimeLocal.includes('AM') && !startTimeLocal.includes('PM') && startTimeLocal.includes(':')) {
                startTimeLocal = utcTimeOfDayToLocal(startTimeLocal, '12hour', exceptionDate);
              }
              if (!endTimeLocal.includes('AM') && !endTimeLocal.includes('PM') && endTimeLocal.includes(':')) {
                endTimeLocal = utcTimeOfDayToLocal(endTimeLocal, '12hour', exceptionDate);
              }

              newDateOverrides[dateISO].push({
                id: `${dateISO}_${newDateOverrides[dateISO].length}`,
                startTime: startTimeLocal,
                endTime: endTimeLocal,
              });
            }
          });

          setDateOverrides(newDateOverrides);
          setUnavailableOverrides(newUnavailableOverrides);
        }

      } catch (error) {
        console.error('Error loading trainer data:', error);
        // Keep default values if loading fails
      } finally {
        setLoading(false);
      }
    };

    loadTrainerData();
  }, [user?.id]);

  const commitNumber = (raw: string, current: number, set: (v: number) => void, exit: () => void) => {
    const t = raw.trim(); if (t === "") { exit(); return; }
    const n = Number(t); if (!Number.isFinite(n)) { exit(); return; }
    set(Math.max(0, Math.floor(n))); exit();
  };

  // Weekly availability
  const [availability, setAvailability] = useState<AvailabilityState>({
    Sunday: { enabled: true, timeRanges: [{ id: "1", startTime: "9:00 AM", endTime: "5:00 PM" }] },
    Monday: { enabled: true, timeRanges: [{ id: "2", startTime: "9:00 AM", endTime: "5:00 PM" }] },
    Tuesday: { enabled: false, timeRanges: [] },
    Wednesday: { enabled: false, timeRanges: [] },
    Thursday: { enabled: false, timeRanges: [] },
    Friday: { enabled: false, timeRanges: [] },
    Saturday: { enabled: false, timeRanges: [] },
  });

  const toggleDay = (day: DayKey, enabled: boolean) =>
    setAvailability((s) => {
      let updatedRanges = s[day].timeRanges;
      // If day is checked ON and has no existing time slots, add the default 9:00 AM - 10:00 AM
      if (enabled && updatedRanges.length === 0) {
        updatedRanges = [{ 
          id: Date.now().toString(), 
          startTime: "9:00 AM", 
          endTime: "10:00 AM" 
        }];
      }
      return { 
        ...s, 
        [day]: { ...s[day], enabled, timeRanges: updatedRanges } 
      };
    });


  const removeTimeRange = (day: DayKey, rangeId: string) =>
    setAvailability((s) => ({
      ...s,
      [day]: { ...s[day], timeRanges: s[day].timeRanges.filter((r) => r.id !== rangeId) },
    }));

  // TimePicker inline for weekly ranges
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [editingTimeRange, setEditingTimeRange] = useState<{ day: DayKey; rangeId: string; timeType: "start" | "end"; currentTime?: string; } | null>(null);

  const handleTimeClick = (day: DayKey, rangeId: string, timeType: "start" | "end") => {
    const currentRange = availability[day].timeRanges.find((r) => r.id === rangeId);
    const currentTime = timeType === "start" ? currentRange?.startTime : currentRange?.endTime;
    setEditingTimeRange({ day, rangeId, timeType, currentTime });
    setShowTimePicker(true);
  };

  const parseTime = (timeStr: string) => {
    const [time, period] = timeStr.split(" ");
    const [hours, minutes] = time.split(":").map(Number);
    let total = (hours % 12) * 60 + minutes;
    if (period === "PM") total += 12 * 60;
    return total;
  };

  // Check if two time ranges overlap
  const rangesOverlap = (range1: TimeRange, range2: TimeRange): boolean => {
    const start1 = parseTime(range1.startTime);
    const end1 = parseTime(range1.endTime);
    const start2 = parseTime(range2.startTime);
    const end2 = parseTime(range2.endTime);

    // Ranges overlap if one starts before the other ends
    return start1 < end2 && start2 < end1;
  };

  // Check if a time range overlaps with any other range (excluding itself)
  const hasOverlap = (dayRanges: TimeRange[], range: TimeRange, excludeRangeId?: string): boolean => {
    return dayRanges.some((r) => {
      if (r.id === excludeRangeId || r.id === range.id) return false;
      return rangesOverlap(r, range);
    });
  };

  // Helper function to add minutes to a time string (capped at 11:59 PM)
  const addMinutesToTime = (timeStr: string, minutes: number): string => {
    const totalMinutes = parseTime(timeStr) + minutes;
    const maxMinutes = 23 * 60 + 59; // 11:59 PM in minutes

    // Cap at 11:59 PM (don't cross over to next day)
    const cappedMinutes = Math.min(totalMinutes, maxMinutes);
    const hours = Math.floor(cappedMinutes / 60);
    const mins = cappedMinutes % 60;

    let hour12 = hours % 12;
    if (hour12 === 0) hour12 = 12;
    const period = hours < 12 ? 'AM' : 'PM';

    return `${hour12}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  const addTimeRange = (day: DayKey) => {
    setAvailability((s) => {
      const existingRanges = s[day].timeRanges;

      // If there are existing ranges, start new range from the end of the last one
      // Otherwise, default to "9:00 AM"
      let startTime = "9:00 AM";
      if (existingRanges.length > 0) {
        // Sort ranges by start time to find the last one
        const sortedRanges = [...existingRanges].sort((a, b) => {
          return parseTime(a.startTime) - parseTime(b.startTime);
        });
        const lastRange = sortedRanges[sortedRanges.length - 1];
        startTime = lastRange.endTime;
      }

      // Check if start time is already at or past 11:59 PM
      const startMinutes = parseTime(startTime);
      const maxMinutes = 23 * 60 + 59; // 11:59 PM

      let endTime: string;
      if (startMinutes >= maxMinutes) {
        // If start is already at 11:59 PM, can't add more - just set end to 11:59 PM
        endTime = "11:59 PM";
      } else {
        // Default end time is 1 hour after start time, but capped at 11:59 PM
        endTime = addMinutesToTime(startTime, 60);
      }

      const newRange: TimeRange = {
        id: Date.now().toString(),
        startTime: startTime,
        endTime: endTime
      };

      // Check for overlaps before adding
      if (hasOverlap(s[day].timeRanges, newRange)) {
        alert("This time range overlaps with an existing time range. Please choose a different time.");
        return s;
      }

      return { ...s, [day]: { ...s[day], timeRanges: [...s[day].timeRanges, newRange] } };
    });
  };

  const handleTimePickerConfirm = (time: string) => {
    if (editingTimeRange) {
      const { day, rangeId, timeType } = editingTimeRange;
      setAvailability((s) => {
        const newTimeRanges = s[day].timeRanges.map((r) => {
          if (r.id !== rangeId) return r;
          let updated = { ...r, [timeType === "start" ? "startTime" : "endTime"]: time };
          let startM = parseTime(updated.startTime);
          let endM = parseTime(updated.endTime);

          // If start time is after or equal to end time, auto-jump the end time forward 1 hour
          if (startM >= endM) {
            updated.endTime = addMinutesToTime(updated.startTime, 60);
            endM = parseTime(updated.endTime);
          }

          // Check for overlaps with other ranges
          if (hasOverlap(s[day].timeRanges, updated, rangeId)) {
            alert("This time range overlaps with another time range. Please choose a different time.");
            return r;
          }

          return updated;
        });
        return { ...s, [day]: { ...s[day], timeRanges: newTimeRanges } };
      });
    }
    setShowTimePicker(false);
    setEditingTimeRange(null);
  };

  /* -------- Date-specific overrides -------- */
  const [dateOverrides, setDateOverrides] = useState<Record<string, DSRange[]>>({});
  const [showDSModal, setShowDSModal] = useState(false);

  const openDSModal = () => setShowDSModal(true);

  const confirmDS = (dateISO: string, ranges: DSRange[]) => {
    setDateOverrides((prev) => {
      const next = { ...prev };
      if (ranges.length === 0) delete next[dateISO];
      else next[dateISO] = ranges;
      return next;
    });
    setShowDSModal(false);
  };

  const removeOverrideRange = (dateISO: string, index: number) => {
    setDateOverrides((prev) => {
      const list = prev[dateISO] ? [...prev[dateISO]] : [];
      list.splice(index, 1);
      const next = { ...prev };
      if (list.length === 0) delete next[dateISO];
      else next[dateISO] = list;
      return next;
    });
  };

  /* -------- Unavailable dates -------- */
  const [unavailableOverrides, setUnavailableOverrides] = useState<Record<string, DSRange[]>>({});
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);

  const openUnavailableModal = () => setShowUnavailableModal(true);

  const confirmUnavailable = (dateISO: string, ranges: DSRange[]) => {
    setUnavailableOverrides((prev) => {
      const next = { ...prev };
      if (ranges.length === 0) delete next[dateISO];
      else next[dateISO] = ranges;
      return next;
    });
    setShowUnavailableModal(false);
  };

  const removeUnavailableRange = (dateISO: string, index: number) => {
    setUnavailableOverrides((prev) => {
      const list = prev[dateISO] ? [...prev[dateISO]] : [];
      list.splice(index, 1);
      const next = { ...prev };
      if (list.length === 0) delete next[dateISO];
      else next[dateISO] = list;
      return next;
    });
  };

  /* ----------------------------- PREVIEW state ---------------------------- */
  const [monthDate, setMonthDate] = useState(() => new Date());
  const computeSelectedDays = () => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const selected: number[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const dateObj = new Date(year, month, d);
      const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' }) as DayKey;
      const weeklyEnabled = availability[dayName]?.enabled && availability[dayName].timeRanges.length > 0;
      const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const hasException = !!dateOverrides[iso]?.length;
      const hasAvailableException = (dateOverrides[iso] || []).length > 0; // all entries are available=true in our editor
      if ((weeklyEnabled && !hasException) || hasAvailableException) {
        selected.push(d);
      }
    }
    return selected;
  };
  const [selectedDays, setSelectedDays] = useState<number[]>(computeSelectedDays());
  const [focusDay, setFocusDay] = useState<number | null>(null);
  const focusedDate = useMemo(
    () => (focusDay == null ? null : new Date(monthDate.getFullYear(), monthDate.getMonth(), focusDay)),
    [focusDay, monthDate]
  );
  const durationMinutes = 90;
  const sampleSlots = ["9:00 am", "10:30 am", "12:00 pm"];
  const prevMonth = () => setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const nextMonth = () => setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  useEffect(() => {
    setSelectedDays(computeSelectedDays());
  }, [monthDate, availability, dateOverrides]);

  const save = async () => {
    if (!user?.id) {
      alert("Please log in to save availability");
      return;
    }

    if (!autoAccept) {
      alert("You must accept the consent to save your availability");
      return;
    }

    // Transform weekly availability to BE format:
    // Convert local times to UTC before sending to backend
    // [{ day: "Sunday", timeSlots: [{ startTime: "03:30", endTime: "11:30" }] (UTC 24-hour format), enabled: true }]
    const today = new Date();
    const weekly = DAY_KEYS.map((day) => {
      const state = availability[day];
      return {
        day,
        enabled: !!state.enabled,
        timeSlots: state.timeRanges.map((r) => {
          // Convert local time (e.g., "9:00 AM") to UTC (e.g., "03:30")
          const startUTC = localTimeOfDayToUTC(r.startTime, '24hour', today);
          const endUTC = localTimeOfDayToUTC(r.endTime, '24hour', today);
          console.log(`Converting ${day} ${r.startTime} -> ${startUTC}, ${r.endTime} -> ${endUTC}`);
          return {
            startTime: startUTC,
            endTime: endUTC
          };
        }),
      };
    });

    // Transform date overrides to flat list expected by BE exceptions schema:
    // Convert local times to UTC before sending
    const availableExceptions = Object.entries(dateOverrides).flatMap(([iso, ranges]) => {
      const exceptionDate = new Date(iso);
      return ranges.map((r) => {
        // Convert local time to UTC for exceptions
        const startUTC = localTimeOfDayToUTC(r.startTime, '24hour', exceptionDate);
        const endUTC = localTimeOfDayToUTC(r.endTime, '24hour', exceptionDate);
        console.log(`Converting exception ${iso} ${r.startTime} -> ${startUTC}, ${r.endTime} -> ${endUTC}`);
        return {
          date: iso,
          startTime: startUTC,
          endTime: endUTC,
          isAvailable: true,
        };
      });
    });

    // Transform unavailable overrides to exceptions with isAvailable: false
    const unavailableExceptions = Object.entries(unavailableOverrides).flatMap(([iso, ranges]) => {
      const exceptionDate = new Date(iso);
      return ranges.map((r) => {
        // Convert local time to UTC for exceptions
        const startUTC = localTimeOfDayToUTC(r.startTime, '24hour', exceptionDate);
        const endUTC = localTimeOfDayToUTC(r.endTime, '24hour', exceptionDate);
        console.log(`Converting unavailable exception ${iso} ${r.startTime} -> ${startUTC}, ${r.endTime} -> ${endUTC}`);
        return {
          date: iso,
          startTime: startUTC,
          endTime: endUTC,
          isAvailable: false,
        };
      });
    });

    // Combine all exceptions
    const exceptions = [...availableExceptions, ...unavailableExceptions];

    const payload = {
      trainerId: user.id,
      availability: weekly,
      exceptions,
      thresholdPeriod: advanceHours, // Map advanceHours to thresholdPeriod
      cooldownPeriod: coolOffHours,   // Map coolOffHours to cooldownPeriod  
      maxSessionsPerDay: dailyLimit,  // Map dailyLimit to maxSessionsPerDay
    };

    try {
      await updateTrainerAvailability(payload);
      navigate(-1);
    } catch (e) {
      console.error("Failed to save availability", e);
      alert("Failed to save availability. Please try again.");
    }
  };

  /* -------------------------------- render -------------------------------- */
  if (loading) {
    return (
      <div className="h-[100dvh] w-full bg-[#F7F7F7] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D62422] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading availability data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="mx-auto w-full min-h-full flex flex-col">
        <Navbar onBack={() => navigate(-1)} />

        <section className="px-5 pt-2">
          <h1
            className="font-manrope font-bold text-[20px] leading-[26px] tracking-[0%] text-[#2D2D2D]"
            style={{
              fontFamily: 'Manrope',
              fontWeight: 700,
              fontStyle: 'normal',
              fontSize: '20px',
              lineHeight: '26px',
              letterSpacing: '0%'
            }}
          >
            Edit Availability
          </h1>
        </section>

        <TabBar tabs={["SESSION & AVAILABILITY", "PREVIEW"]} active={tab} onChange={setTab} />

        <main className="px-5 pt-4 pb-10">
          {tab === 0 && (
            <div className="space-y-6">
              {/* consent */}
              <div className="flex items-start gap-3 py-4">
                <SquareCheckbox checked={autoAccept} onChange={setAutoAccept} ariaLabel="Auto accept consent" />
                <p className="text-[13px] leading-5 text-[#2D2D2D]">
                  By setting my availability, I understand that the system will automatically accept bookings for any
                  available timeslots.
                </p>
              </div>

              {/* SESSION LIMITS */}
              <Title text="// SESSION LIMITS" />

              {/* settings */}
              <SessionSettingCard
                title="Advance Booking"
                valueText={`Book before ${advanceHours} hrs`}
                onValueClick={() => { setEditingAdvance(true); setEditingCoolOff(false); setEditingDaily(false); }}
                helper="Set the minimum hours required before a session can be booked."
                isEditing={editingAdvance}
              >
                <div className="flex flex-row items-start gap-5 flex-1 h-12">
                  <div className="flex flex-row justify-center items-center px-5 gap-3 flex-1 h-12 border border-[#B1B1B1]">
                    <input ref={advanceRef} type="text" inputMode="numeric" defaultValue={advanceHours}
                      onKeyDown={(e) => { if (e.key === "Enter") { commitNumber((e.target as HTMLInputElement).value, advanceHours, setAdvanceHours, () => { }); } if (e.key === "Escape") setEditingAdvance(false); }}
                      onBlur={(e) => commitNumber(e.target.value, advanceHours, setAdvanceHours, () => { })}
                      className="w-full text-center text-[14px] bg-transparent outline-none" />
                  </div>
                </div>
                <div className="flex items-start w-[60px] h-12">
                  <div className="flex justify-center items-center px-5 w-full h-12 border border-[#DDDDDD]">
                    <div className="text-[#DEDEDE] text-[14px] select-none">hr</div>
                  </div>
                </div>
              </SessionSettingCard>

              <SessionSettingCard
                title="Session Cool-off Time"
                valueText={`${coolOffHours} hrs Rest`}
                onValueClick={() => { setEditingCoolOff(true); setEditingAdvance(false); setEditingDaily(false); }}
                helper="Set the required break time between consecutive sessions."
                isEditing={editingCoolOff}
              >
                <div className="flex flex-row items-start gap-5 flex-1 h-12">
                  <div className="flex flex-row justify-center items-center px-5 gap-3 flex-1 h-12 border border-[#B1B1B1]">
                    <input ref={coolOffRef} type="text" inputMode="numeric" defaultValue={coolOffHours}
                      onKeyDown={(e) => { if (e.key === "Enter") { commitNumber((e.target as HTMLInputElement).value, coolOffHours, setCoolOffHours, () => { }); } if (e.key === "Escape") setEditingCoolOff(false); }}
                      onBlur={(e) => commitNumber(e.target.value, coolOffHours, setCoolOffHours, () => { })}
                      className="w-full text-center text-[14px] bg-transparent outline-none" />
                  </div>
                </div>
                <div className="flex items-start w-[60px] h-12">
                  <div className="flex justify-center items-center px-5 w-full h-12 border border-[#DDDDDD]">
                    <div className="text-[#DEDEDE] text-[14px] select-none">hr</div>
                  </div>
                </div>
              </SessionSettingCard>

              <SessionSettingCard
                title="Daily Session Limit"
                valueText={`${dailyLimit} per day`}
                onValueClick={() => { setEditingDaily(true); setEditingAdvance(false); setEditingCoolOff(false); }}
                helper="Limit the total number of sessions you can take in a day."
                isEditing={editingDaily}
              >
                <div className="flex items-start w-full h-12">
                  <div className="flex flex-row justify-center items-center px-5 gap-3 w-full h-12 border border-[#B1B1B1]">
                    <input ref={dailyRef} type="text" inputMode="numeric" defaultValue={dailyLimit}
                      onKeyDown={(e) => { if (e.key === "Enter") { commitNumber((e.target as HTMLInputElement).value, dailyLimit, setDailyLimit, () => { }); } if (e.key === "Escape") setEditingDaily(false); }}
                      onBlur={(e) => commitNumber(e.target.value, dailyLimit, setDailyLimit, () => { })}
                      className="w-full text-center text-[14px] bg-transparent outline-none" />
                  </div>
                </div>
              </SessionSettingCard>

              {/* AVAILABILITY */}
              <Title text="// AVAILABILITY" />

              <div className="space-y-3">
                {DAY_KEYS.map((day) => {
                  const s = availability[day];
                  return (
                    <DayCard
                      key={day}
                      day={day}
                      state={s}
                      onToggle={(en) => toggleDay(day, en)}
                      onAddTimeRange={() => addTimeRange(day)}
                      onRemoveTimeRange={(rangeId) => removeTimeRange(day, rangeId)}
                      onStartTimeClick={(rangeId) => handleTimeClick(day, rangeId, "start")}
                      onEndTimeClick={(rangeId) => handleTimeClick(day, rangeId, "end")}
                    />
                  );
                })}
              </div>

              {/* Date-specific hours section */}
              {Object.keys(dateOverrides).length === 0 ? (
                /* Show only button when no overrides exist */
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 text-[#EB2726] font-semibold w-full"
                  onClick={openDSModal}
                >
                  <Plus className="w-4 h-4" />
                  DATE SPECIFIC HOURS
                </button>
              ) : (
                /* Show content when overrides exist */
                <div className="border border-[#E5E5E5] bg-white px-4 py-4">
                  <div className="text-[14px] font-extrabold">Date-specific hours</div>
                  <div className="text-[12px] text-[#717171] mt-1">
                    Override your availability for specific dates when your hours differ from your regular weekly hours.
                  </div>

                  {/* list of overrides */}
                  <div className="mt-4 space-y-3">
                    {Object.keys(dateOverrides).sort(sortISO).map((iso) => {
                      const slots = dateOverrides[iso];
                      if (!slots || slots.length === 0) return null;

                      const [first, ...rest] = slots;

                      return (
                        <div key={iso}>
                          {/* row 1: DATE (left)  |  FIRST SLOT (right) */}
                          <div className="flex items-center justify-between">
                            <div className="text-[14px] text-[#2D2D2D]">
                              {fmtMonthDayYear(iso)}
                            </div>

                            <div className="flex items-center gap-2">
                              <div className="text-[14px]">
                                {formatTimeSlot(first.startTime, first.endTime)}
                              </div>
                              <button
                                className="w-6 h-6 grid place-items-center border border-[#DEDEDE] bg-white"
                                onClick={() => removeOverrideRange(iso, 0)}
                                aria-label="Remove slot"
                              >
                                <X className="w-3.5 h-3.5 text-[#8A8A8A]" />
                              </button>
                            </div>
                          </div>

                          {/* subsequent slots: align under the first slot (no date on the left) */}
                          {rest.map((r, idx) => (
                            <div
                              key={`${iso}_${idx + 1}`}
                              className="mt-1 flex items-center justify-end"
                            >
                              <div className="flex items-center gap-2">
                                <div className="text-[14px]">
                                  {formatTimeSlot(r.startTime, r.endTime)}
                                </div>
                                <button
                                  className="w-6 h-6 grid place-items-center border border-[#DEDEDE] bg-white"
                                  onClick={() => removeOverrideRange(iso, idx + 1)}
                                  aria-label="Remove slot"
                                >
                                  <X className="w-3.5 h-3.5 text-[#8A8A8A]" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA: open modal - shown at bottom of content */}
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 text-[#EB2726] font-semibold mt-4 w-full"
                    onClick={openDSModal}
                  >
                    <Plus className="w-4 h-4" />
                    DATE SPECIFIC HOURS
                  </button>
                </div>
              )}

              {/* Mark as unavailable section */}
              {Object.keys(unavailableOverrides).length === 0 ? (
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 text-[#EB2726] font-semibold w-full mt-4"
                  onClick={openUnavailableModal}
                >
                  <Plus className="w-4 h-4" />
                  MARK AS UNAVAILABLE
                </button>
              ) : (
                <div className="border border-[#E5E5E5] bg-white px-4 py-4 mt-4">
                  <div className="text-[14px] font-extrabold">Unavailable dates</div>
                  <div className="text-[12px] text-[#717171] mt-1">
                    Override your availability for specific dates when you are unavailable.
                  </div>

                  {/* list of unavailable overrides */}
                  <div className="mt-4 space-y-3">
                    {Object.keys(unavailableOverrides).sort(sortISO).map((iso) => {
                      const slots = unavailableOverrides[iso];
                      if (!slots || slots.length === 0) return null;

                      const [first, ...rest] = slots;

                      return (
                        <div key={iso}>
                          {/* row 1: DATE (left)  |  FIRST SLOT (right) */}
                          <div className="flex items-center justify-between">
                            <div className="text-[14px] text-[#2D2D2D]">
                              {fmtMonthDayYear(iso)}
                            </div>

                            <div className="flex items-center gap-2">
                              <div className="text-[14px]">
                                {formatTimeSlot(first.startTime, first.endTime)}
                              </div>
                              <button
                                className="w-6 h-6 grid place-items-center border border-[#DEDEDE] bg-white"
                                onClick={() => removeUnavailableRange(iso, 0)}
                                aria-label="Remove slot"
                              >
                                <X className="w-3.5 h-3.5 text-[#8A8A8A]" />
                              </button>
                            </div>
                          </div>

                          {/* subsequent slots: align under the first slot (no date on the left) */}
                          {rest.map((r, idx) => (
                            <div
                              key={`${iso}_${idx + 1}`}
                              className="mt-1 flex items-center justify-end"
                            >
                              <div className="flex items-center gap-2">
                                <div className="text-[14px]">
                                  {formatTimeSlot(r.startTime, r.endTime)}
                                </div>
                                <button
                                  className="w-6 h-6 grid place-items-center border border-[#DEDEDE] bg-white"
                                  onClick={() => removeUnavailableRange(iso, idx + 1)}
                                  aria-label="Remove slot"
                                >
                                  <X className="w-3.5 h-3.5 text-[#8A8A8A]" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>

                  {/* CTA: open modal */}
                  <button
                    type="button"
                    className="inline-flex items-center justify-center gap-2 text-[#EB2726] font-semibold mt-4 w-full"
                    onClick={openUnavailableModal}
                  >
                    <Plus className="w-4 h-4" />
                    MARK AS UNAVAILABLE
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === 1 && (
            <div>
              <div className="flex items-start gap-3 mb-4 py-4">
                <SquareCheckbox checked={autoAccept} onChange={setAutoAccept} ariaLabel="Auto accept consent" />
                <p className="text-[13px] leading-5 text-[#2D2D2D]">
                  By setting my availability, I understand that the system will automatically accept bookings for any available timeslots.
                </p>
              </div>

              <Title text="// AVAILABILITY" />
              <div className="p-2"></div>
              {focusDay == null ? (
                <PreviewMonth
                  monthDate={monthDate}
                  selectedDays={selectedDays}
                  onSelectDay={(d) => setFocusDay(d)}
                  onPrevMonth={() => {
                    setFocusDay(null);        // ensure calendar view
                    prevMonth();
                  }}
                  onNextMonth={() => {
                    setFocusDay(null);        // ensure calendar view
                    nextMonth();
                  }}
                />
              ) : (
                focusedDate && (
                  <PreviewDaySlots
                    date={focusedDate}
                    durationMinutes={durationMinutes}
                    slots={(function () {
                      const dayName = focusedDate.toLocaleDateString('en-US', { weekday: 'long' }) as DayKey;
                      const weekly = availability[dayName];
                      const iso = `${focusedDate.getFullYear()}-${String(focusedDate.getMonth() + 1).padStart(2, '0')}-${String(focusedDate.getDate()).padStart(2, '0')}`;
                      const overrides = dateOverrides[iso] || [];
                      if (overrides.length > 0) {
                        return overrides.map(r => formatTimeSlot(r.startTime, r.endTime));
                      }
                      if (weekly?.enabled) {
                        return weekly.timeRanges.map(r => formatTimeSlot(r.startTime, r.endTime));
                      }
                      return [];
                    })()}
                    onBackToMonth={() => setFocusDay(null)}  // any “date change” returns to calendar
                  />
                )
              )}
            </div>
          )}

          {/* SAVE */}
          <div className="pt-8 pb-[max(16px,env(safe-area-inset-bottom))]">
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={() => navigate(-1)}
                className="w-[52px] h-[52px] bg-white border border-[#CFCFCF] grid place-items-center"
                aria-label="Close"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M6 18L18 6M6 6l12 12"
                    stroke="#6B6B6B"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              <PrimaryButton
                label="SAVE AND EXIT"
                onClick={save}
                className="!flex-1"
                disabled={!autoAccept}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Weekly TimePicker Modal */}
      {showTimePicker && editingTimeRange && (
        <TimePicker onConfirm={handleTimePickerConfirm} onClose={() => { setShowTimePicker(false); setEditingTimeRange(null); }} initialTime={editingTimeRange.currentTime || "9:00 AM"} />
      )}

      {/* Date-specific hours Modal */}
      {showDSModal && (
        <DateSpecificHoursModal
          onClose={() => setShowDSModal(false)}
          onConfirm={confirmDS}
          existingOverrides={dateOverrides}
        />
      )}

      {showUnavailableModal && (
        <UnavailableDatesModal
          onClose={() => setShowUnavailableModal(false)}
          onConfirm={confirmUnavailable}
          existingOverrides={unavailableOverrides}
        />
      )}
    </div>
  );
}
